import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ServiceCard = ({ service, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`group relative glass-morph bg-primary/20 backdrop-blur-md rounded-2xl border border-white/10 hover:border-brand-cyan/30 transition-all duration-500 overflow-hidden transform hover:-translate-y-2 ${
        index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
      } flex flex-col md:flex`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <div className="relative md:w-1/2 h-64 md:h-auto overflow-hidden">
        <Image
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Floating Stats */}
        <div className={`absolute top-4 left-4 transition-all duration-500 ${isHovered ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'}`}>
          <div className="glass-morph bg-primary/80 backdrop-blur-md rounded-lg p-3 border border-brand-cyan/30">
            <div className="flex items-center space-x-4">
              <div className="text-center">
                <div className="text-lg font-bold text-white">{service.stats.projects}</div>
                <div className="text-xs text-white">Projects</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-brand-green">{service.stats.success}</div>
                <div className="text-xs text-white">Success Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Service Category Badge */}
        <div className="absolute top-4 right-4">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${service.categoryColor}`}>
            {service.category}
          </span>
        </div>
      </div>

      {/* Content Section */}
      <div className="md:w-1/2 p-8 flex flex-col justify-between">
        <div>
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className={`p-2 rounded-lg mr-3 ${service.iconBg}`}>
                <Icon name={service.icon} size={24} className={service.iconColor} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-1 font-heading">{service.title}</h3>
                <p className="text-sm text-brand-cyan font-body">{service.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-white/90 mb-6 leading-relaxed font-body">{service.description}</p>

          {/* Key Features */}
          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3 font-heading">Key Capabilities:</h4>
            <div className="grid grid-cols-1 gap-2">
              {service.features.map((feature, featureIndex) => (
                <div key={featureIndex} className="flex items-center">
                  <Icon name="CheckCircle2" size={16} className="text-brand-green mr-2 flex-shrink-0" />
                  <span className="text-sm text-white font-body">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Technologies */}
          <div className="mb-6">
            <h4 className="font-semibold text-white mb-3 font-heading">Technologies & Frameworks:</h4>
            <div className="flex flex-wrap gap-2">
              {service.technologies.map((tech, techIndex) => (
                <span
                  key={techIndex}
                  className="px-3 py-1 glass-morph bg-brand-purple/20 text-brand-purple border border-brand-purple/30 rounded-full text-xs font-medium hover:bg-brand-purple/30 hover:border-brand-purple/50 transition-colors duration-200 backdrop-blur-sm"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Client Testimonial */}
          <div className="glass-morph bg-secondary/30 backdrop-blur-sm rounded-lg p-4 mb-6 border border-white/10">
            <div className="flex items-start">
              <Icon name="Quote" size={20} className="text-brand-cyan mr-3 flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm text-white/90 italic mb-2 font-body">"{service.testimonial.quote}"</p>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-brand-cyan to-brand-purple rounded-full mr-2"></div>
                  <div>
                    <p className="text-xs font-semibold text-white font-body">{service.testimonial.author}</p>
                    <p className="text-xs text-white font-body">{service.testimonial.position}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="default"
            className="flex-1 glass-morph bg-brand-cyan/20 hover:bg-brand-cyan/30 text-white border border-brand-cyan/50 backdrop-blur-md"
          >
            <Icon name="ArrowRight" size={16} className="mr-2" />
            Explore Service
          </Button>
          <Button
            variant="outline"
            className="flex-1 glass-morph border-2 border-brand-purple/50 text-white hover:bg-brand-purple/20 hover:border-brand-purple backdrop-blur-md"
          >
            <Icon name="MessageCircle" size={16} className="mr-2" />
            Discuss Project
          </Button>
        </div>

        {/* ROI Indicator */}
        <div className={`mt-4 transition-all duration-500 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white font-body">Average ROI:</span>
            <span className="font-bold text-brand-green font-body">{service.roi}</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 mt-1">
            <div
              className="bg-gradient-to-r from-brand-green to-brand-cyan h-2 rounded-full transition-all duration-1000"
              style={{ width: isHovered ? service.roiPercentage : '0%' }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceCard;